import { useState } from "react";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import type { InputSize } from "@design-system/Input/Input.types";
import { useAnatomyMode } from "./componentAnatomyMode";
import { FieldPlaygroundMatrix } from "./FieldPlaygroundMatrix";
import {
    BooleanControl,
    SelectControl,
    TextControl,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

const inputSizes: readonly InputSize[] = ["md", "lg", "xl"];

export const FieldPlayground = () => {
    const [label, setLabel] = useState("Business email");
    const [isOptional, setIsOptional] = useState(false);
    const [labelTooltip, setLabelTooltip] = useState(
        "Used to send account notices",
    );
    const [description, setDescription] = useState("Business address");
    const [error, setError] = useState("");
    const [isInvalid, setIsInvalid] = useState(false);
    const [inputSize, setInputSize] = useState<InputSize>("md");

    const tooltipContent = labelTooltip === "" ? undefined : labelTooltip;
    const descriptionContent = description === "" ? undefined : description;
    const errorContent = error === "" ? undefined : error;
    const anatomy = useAnatomyMode({
        component: "field",
        variantId: "default",
        isDisabled: false,
        isInvalid: isInvalid || errorContent !== undefined,
        fieldContent: {
            label,
            isOptional,
            hasTooltip: tooltipContent !== undefined,
            message: errorContent ?? descriptionContent ?? null,
        },
    });

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Content</h2>
                        <TextControl
                            label="Label"
                            value={label}
                            onChange={setLabel}
                        />
                        <TextControl
                            label="Tooltip"
                            value={labelTooltip}
                            onChange={setLabelTooltip}
                        />
                        <TextControl
                            label="Description"
                            value={description}
                            onChange={setDescription}
                        />
                        <TextControl
                            label="Error"
                            value={error}
                            onChange={setError}
                        />
                    </section>
                    <section>
                        <h2>Options</h2>
                        <BooleanControl
                            label="Optional"
                            checked={isOptional}
                            onChange={setIsOptional}
                        />
                        <BooleanControl
                            label="Invalid"
                            checked={isInvalid}
                            onChange={setIsInvalid}
                        />
                        <SelectControl
                            label="Input size"
                            options={inputSizes}
                            value={inputSize}
                            onChange={setInputSize}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive
                        ? "x-ray · Field"
                        : `Field · Input ${inputSize}`
                }
                belowStage={<FieldPlaygroundMatrix />}
                output={JSON.stringify(
                    {
                        label,
                        optional: isOptional,
                        labelTooltip: tooltipContent ?? null,
                        description: descriptionContent ?? null,
                        error: errorContent ?? null,
                        invalid: isInvalid,
                        inputSize,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <div style={{ width: 240 }}>
                        <Field
                            label={label}
                            optional={isOptional}
                            labelTooltip={tooltipContent}
                            description={descriptionContent}
                            error={errorContent}
                            invalid={isInvalid}
                        >
                            <Input
                                size={inputSize}
                                placeholder="name@company.com"
                            />
                        </Field>
                    </div>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
